import { takeLatest, call, put, takeEvery } from "redux-saga/effects";
import DOMPurify from "dompurify";
import {
  FETCH_DATA_TAG_REPORTS,
  FETCH_DATA_TAG_REPORT_CONTENT,
  SET_DATA_TAG_REPORTS_LOADER,
  SET_DATA_TAG_ALL_REPORTS,
  SET_DATA_TAG_SNACKBAR,
  SET_DATA_TAG_CONTENT_ACTION,
  DATA_TAG_REPORTS_API_URL,
  fetchDataTagReportContentAPI,
  fetchDataSourcesDataTag,
  SET_DATA_SOURCES_DATA_TAG,
  FETCH_DATA_SOURCES_DATA_TAG,
  DOWNLOAD_DATA_TAG_REPORT,
  VIEW_DATA_TAG_REPORT,
  UPLOAD_DATA_TAG_REPORT,
  SET_DATA_TAG_DOWNLOAD_LOADER,
  UPLOAD_DATA_TAG_REPORT_SUCCESS,
  UPLOAD_DATA_TAG_REPORT_FAILURE,
  DATA_TAG_UPLOAD_API_URL,
  fetchOldDataTagReportViewAPI,
  SET_IS_DATABASE,
  SET_DATA_TAG_UPLOAD_LOADER,
} from "../../features/Data Tag/DataTagConstants";
import { getAxiosHeaders, getService } from "../../library/RestAPI";
import axios from "axios";

function* fetchDataTagReports(action) {
  yield put({ type: SET_DATA_TAG_REPORTS_LOADER, payload: true });

  const {
    reportType,
    periodType,
    page = 0,
    size = 20,
    search = "",
  } = action.payload || {};

  const queryParams = new URLSearchParams();

  if (reportType) {
    queryParams.append("reportType", reportType);
  }

  if (periodType) {
    queryParams.append("periodType", periodType);
  }

  queryParams.append("page", page);
  queryParams.append("size", size);

  if (search) {
    queryParams.append("search", search);
  }

  const options = {
    method: "GET",
    url: `${DATA_TAG_REPORTS_API_URL}?${queryParams.toString()}`,
    headers: getAxiosHeaders(true),
  };

  try {
    const res = yield getService(options);

    if (res && res.status === 200) {
      yield put({
        type: SET_DATA_TAG_ALL_REPORTS,
        payload: res?.data,
      });

      yield put({
        type: SET_DATA_TAG_REPORTS_LOADER,
        payload: false,
      });
    } else {
      yield put({
        type: SET_DATA_TAG_ALL_REPORTS,
        payload: {
          reports: [],
          totalElements: 0,
        },
      });

      yield put({
        type: SET_DATA_TAG_REPORTS_LOADER,
        payload: false,
      });

      yield put({
        type: SET_DATA_TAG_SNACKBAR,
        payload: {
          status: "error",
          message: "Failed to fetch reports",
        },
      });
    }
  } catch (error) {
    console.error("Error fetching Data Tag reports:", error);

    yield put({
      type: SET_DATA_TAG_ALL_REPORTS,
      payload: {
        reports: [],
        totalElements: 0,
      },
    });

    yield put({
      type: SET_DATA_TAG_REPORTS_LOADER,
      payload: false,
    });

    yield put({
      type: SET_DATA_TAG_SNACKBAR,
      payload: {
        status: "error",
        message: "Error fetching reports",
      },
    });
  }
}

function* downloadDataTagReport(action) {
  const { downloadUrl, fileName } = action.payload;
  yield put({ type: SET_DATA_TAG_DOWNLOAD_LOADER, payload: true });

  try {
    const options = {
      method: "GET",
      url: downloadUrl,
      headers: getAxiosHeaders(true),
      responseType: "blob",
    };

    const response = yield getService(options);

    if (response && response.data) {
      const contentType = response.headers["content-type"] || "application/pdf";

      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = blobUrl;
      link.download = fileName || "report.pdf";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);

      yield put({
        type: SET_DATA_TAG_DOWNLOAD_LOADER,
        payload: false,
      });

      yield put({
        type: SET_DATA_TAG_SNACKBAR,
        payload: {
          status: "success",
          message: "File downloaded successfully",
        },
      });
    } else {
      yield put({
        type: SET_DATA_TAG_DOWNLOAD_LOADER,
        payload: false,
      });

      yield put({
        type: SET_DATA_TAG_SNACKBAR,
        payload: {
          status: "error",
          message: "Failed to download file",
        },
      });
    }
  } catch (error) {
    console.error("Error downloading file:", error);

    yield put({
      type: SET_DATA_TAG_DOWNLOAD_LOADER,
      payload: false,
    });

    yield put({
      type: SET_DATA_TAG_SNACKBAR,
      payload: {
        status: "error",
        message: "Failed to download file",
      },
    });
  }
}

const sanitizeReportHtml = (html) =>
  DOMPurify.sanitize(html, { WHOLE_DOCUMENT: true });

function openReportPreviewWindow(html, previewTitle) {
  const previewWindow = window.open("", "_blank");

  if (!previewWindow) {
    return;
  }

  previewWindow.document.open();
  previewWindow.document.write(sanitizeReportHtml(html));
  previewWindow.document.close();

  if (previewTitle) {
    previewWindow.document.title = previewTitle;
  }
}

function printReportHtml(html) {
  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";

    document.body.appendChild(iframe);

    const cleanup = () => {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }

      resolve();
    };

    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } finally {
        setTimeout(cleanup, 1000);
      }
    };

    const doc = iframe.contentWindow.document;

    doc.open();
    doc.write(sanitizeReportHtml(html));
    doc.close();
  });
}

function downloadReportAsPdf(html, report) {
  const container = document.createElement("div");

  container.innerHTML = sanitizeReportHtml(html);
  container.style.position = "fixed";
  container.style.left = "-10000px";
  container.style.top = "0";

  document.body.appendChild(container);

  const rangeSlug = report?.periodDisplayRange
    ? report.periodDisplayRange.replace(/\s+/g, "-")
    : report?.id || "report";

  const fileName = `insights-report-${rangeSlug}.pdf`;

  // return html2pdf()
  //   .set({
  //     margin: 10,
  //     filename: fileName,
  //     html2canvas: { scale: 2, useCORS: true },
  //     jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
  //   })
  //   .from(container)
  //   .save()
  //   .then(() => {
  //     document.body.removeChild(container);
  //   })
  //   .catch((err) => {
  //     document.body.removeChild(container);
  //     throw err;
  //   });
}

function* fetchDataTagReportContent(action) {
  const { reportId, mode, report, previewTitle, tenantId } =
    action.payload || {};

  yield put({
    type: SET_DATA_TAG_CONTENT_ACTION,
    payload: {
      reportId,
      mode,
    },
  });

  try {
    if (!report?.isInsightsReport) {
      const viewUrl = fetchOldDataTagReportViewAPI(
        reportId,
        report?.reportName,
        tenantId,
        report?.filePath,
      );

      if (mode === "preview") {
        yield call(viewDataTagReport, {
          payload: {
            viewUrl,
          },
        });
      } else if (mode === "download") {
        const downloadUrl = viewUrl.replace("?mode=view", "?mode=download");

        const fileName = `${report?.reportName || reportId}.pdf`;

        yield call(downloadDataTagReport, {
          payload: {
            downloadUrl,
            fileName,
          },
        });
      }

      return;
    }

    const options = {
      method: "GET",
      url: fetchDataTagReportContentAPI(reportId),
      headers: getAxiosHeaders(true),
      responseType: "text",
    };

    const res = yield getService(options);

    if (res && res.status === 200 && typeof res.data === "string") {
      const html = res.data;

      if (mode === "preview") {
        openReportPreviewWindow(html, previewTitle);
      } else if (mode === "print") {
        yield call(printReportHtml, html);
      } else if (mode === "download") {
        yield call(downloadReportAsPdf, html, report);
      }
    } else {
      yield put({
        type: SET_DATA_TAG_SNACKBAR,
        payload: {
          status: "error",
          message: "Failed to load report content",
        },
      });
    }
  } catch (error) {
    console.error("Error fetching report content:", error);

    yield put({
      type: SET_DATA_TAG_SNACKBAR,
      payload: {
        status: "error",
        message: "Error loading report content",
      },
    });
  } finally {
    yield put({
      type: SET_DATA_TAG_CONTENT_ACTION,
      payload: {
        reportId: null,
        mode: null,
      },
    });
  }
}

function* fetchDataSourcesListDataTag(action) {
  try {
    const options = {
      method: "GET",
      url: fetchDataSourcesDataTag(),
      headers: getAxiosHeaders(true),
    };

    const response = yield getService(options);

    if (response && response.status === 200) {
      yield put({
        type: SET_DATA_SOURCES_DATA_TAG,
        payload: response?.data,
      });
    } else {
      console.log("Something Went Wrong");
    }
  } catch (error) {
    console.error("Error viewing file:", error);
  }
}

function* viewDataTagReport(action) {
  const { viewUrl } = action.payload;

  yield put({
    type: SET_DATA_TAG_DOWNLOAD_LOADER,
    payload: true,
  });

  try {
    const options = {
      method: "GET",
      url: viewUrl,
      headers: getAxiosHeaders(true),
      responseType: "blob",
    };

    const response = yield getService(options);

    if (response && response.data) {
      const contentType = response.headers["content-type"] || "application/pdf";

      const blob = new Blob([response.data], {
        type: contentType,
      });

      const blobUrl = window.URL.createObjectURL(blob);

      window.open(blobUrl, "_blank");

      yield put({
        type: SET_DATA_TAG_DOWNLOAD_LOADER,
        payload: false,
      });
    } else {
      yield put({
        type: SET_DATA_TAG_DOWNLOAD_LOADER,
        payload: false,
      });

      yield put({
        type: SET_DATA_TAG_SNACKBAR,
        payload: {
          status: "error",
          message: "Failed to view file",
        },
      });
    }
  } catch (error) {
    console.error("Error viewing file:", error);

    yield put({
      type: SET_DATA_TAG_DOWNLOAD_LOADER,
      payload: false,
    });

    yield put({
      type: SET_DATA_TAG_SNACKBAR,
      payload: {
        status: "error",
        message: "Failed to view file",
      },
    });
  }
}

function* uploadDataTagReport(action) {
  yield put({
    type: SET_DATA_TAG_UPLOAD_LOADER,
    payload: true,
  });
  const {
    file,
    reportName,
    description,
    section,
    reportPeriodStart,
    reportPeriodEnd,
    expiryDate,
    reportType,
  } = action.payload;

  try {
    const formData = new FormData();

    formData.append("file", file);
    formData.append("reportName", reportName);

    if (description) {
      formData.append("description", description);
    }

    if (section) {
      formData.append("sections", section);
    }

    formData.append("reportPeriodStart", reportPeriodStart);
    formData.append("reportPeriodEnd", reportPeriodEnd);
    formData.append("expiryDate", expiryDate);
    formData.append("type", reportType);

    const headers = getAxiosHeaders(true);

    const response = yield call(axios.post, DATA_TAG_UPLOAD_API_URL, formData, {
      headers: {
        ...headers,
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );

        window.dispatchEvent(
          new CustomEvent("uploadProgress", {
            detail: percentCompleted,
          }),
        );
      },
    });

    if (response && (response.status === 200 || response.status === 201)) {
      yield put({
        type: UPLOAD_DATA_TAG_REPORT_SUCCESS,
        payload: response.data.message || "Report uploaded successfully!",
      });

      yield put({
        type: FETCH_DATA_TAG_REPORTS,
        payload: {
          reportType: reportType,
        },
      });
      yield put({
        type: SET_DATA_TAG_UPLOAD_LOADER,
        payload: false,
      });
    } else {
      yield put({
        type: UPLOAD_DATA_TAG_REPORT_FAILURE,
        payload: "Upload failed. Please try again.",
      });
      yield put({
        type: SET_DATA_TAG_UPLOAD_LOADER,
        payload: false,
      });
    }
  } catch (error) {
    console.error("Error uploading report:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to upload report";

    yield put({
      type: UPLOAD_DATA_TAG_REPORT_FAILURE,
      payload: errorMessage,
    });
    yield put({
      type: SET_DATA_TAG_UPLOAD_LOADER,
      payload: false,
    });
  }
}

export function* DataTagReportsSaga() {
  yield takeLatest(FETCH_DATA_TAG_REPORTS, fetchDataTagReports);
  yield takeLatest(FETCH_DATA_TAG_REPORT_CONTENT, fetchDataTagReportContent);
  yield takeLatest(DOWNLOAD_DATA_TAG_REPORT, downloadDataTagReport);
  yield takeLatest(VIEW_DATA_TAG_REPORT, viewDataTagReport);
  yield takeLatest(UPLOAD_DATA_TAG_REPORT, uploadDataTagReport);
  yield takeEvery(FETCH_DATA_SOURCES_DATA_TAG, fetchDataSourcesListDataTag);
}
